import { Modal, message } from "antd";
import { useMemo, useState } from "react";
import axiosConfig from "../util/axios";
import requestErrorHandler from "../util/requestErrorHandler";
import uploadToS3 from "../util/uploadToS3";
import { Input } from "./ui/input";
import { Loader2, Upload } from "lucide-react";
import TextArea from "antd/es/input/TextArea";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import useAuth from "../hooks/useAuth";

type Props = {
  open: boolean;
  user: User | null;
  onCancel: () => void;
};

export default function EditProfileModal({
  open,
  user,
  onCancel,
}: Props) {
  const { getLoggedIn } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    companyName: user?.fleetOwner.companyName || "",
    companyRegistrationNumber: user?.fleetOwner.companyRegistrationNumber || "",
    address: user?.fleetOwner.address || "",
    cacCertificate: user?.fleetOwner.cacCertificate || "",
  });
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const updatedFields = useMemo(() => {
    const defaultUser = {
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      companyName: user?.fleetOwner.companyName || "",
      companyRegistrationNumber: user?.fleetOwner.companyRegistrationNumber || "",
      address: user?.fleetOwner.address || "",
      cacCertificate: user?.fleetOwner.cacCertificate || "",      
    }

    return diffObjects(defaultUser, formData)
  }, [user, formData])

  function diffObjects(objA: any, objB: any) {
    const objC: Record<string, any> = {};
    const keys = new Set([...Object.keys(objA), ...Object.keys(objB)]);
    for (const k of keys) {
      if (objA[k] !== objB[k] && k in objB) {
        objC[k] = objB[k];
      }
    }
    return objC;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };  

  const handleFileUpload = async () => {
    if (!file) return alert("Please select a file to upload.");

    setUploading(true);
    try {
      await uploadToS3({
        file,
        data: {},
        filename: file.name,
        onSuccess: (url: string) => {
          setFormData((prev) => ({ ...prev, cacCertificate: url }));
        },
        onError: (error: any) => {
          console.error("Upload failed:", error);
        },
      });
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };  

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await axiosConfig.put("/fleet/profile-update", {
        ...updatedFields
      });
      message.success("Profile Updated successfully!");
      getLoggedIn(onCancel);
    } catch (error) {
      requestErrorHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      footer={null}
      open={open}
      title="Update Profile Information"
      onCancel={onCancel}
    >
    <div>
      <form
        onSubmit={handleSubmit}
        className={cn("space-y-8", isSubmitting ? "opacity-50" : "opacity-100")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="name" className="block font-medium">
                Your name
              </label>
              <Input
                disabled={isSubmitting}
                id="name"
                name="name"
                placeholder="Enter Full Name"
                value={formData.name}
                defaultValue={user?.name}
                onChange={handleChange}
                className="pl-3 bg-gray-200 border-none"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="block font-medium">
                Email address*
              </label>
              <Input
                disabled={isSubmitting}
                id="email"
                name="email"
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange}
                required
                className="pl-3 bg-gray-200 border-none"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="companyName" className="block font-medium">
                Company name
              </label>
              <Input
                disabled={isSubmitting}
                id="companyName"
                name="companyName"
                placeholder="Enter company name"
                value={formData.companyName}
                onChange={handleChange}
                className="pl-3 bg-gray-200 border-none"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="companyRegistration"
                className="block font-medium"
              >
                Company registration number
              </label>
              <Input
                disabled={isSubmitting}
                id="companyRegistrationNumber"
                name="companyRegistrationNumber"
                placeholder="Enter company number"
                value={formData.companyRegistrationNumber}
                onChange={handleChange}
                className="pl-3 bg-gray-200 border-none"
              />
            </div>            
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="cacCertificate" className="block font-medium">
                CAC Certificate
              </label>
              <div className="border border-dashed border-blue-500 rounded-md p-2 flex flex-col items-center justify-center bg-blue-50">
                <input
                  disabled={isSubmitting}
                  type="file"
                  id="cacCertificate"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="cacCertificate"
                  className="flex items-center gap-2 text-blue-500 cursor-pointer"
                >
                  <Upload className="w-5" />
                  <span>{file ? file.name : "Upload"}</span>
                </label>
                {file && (
                  <button
                    type="button"
                    className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md"
                    onClick={handleFileUpload}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="phone" className="block font-medium">
                Phone number
              </label>
              <Input
                disabled={isSubmitting}
                id="phone"
                name="phone"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={handleChange}
                className="pl-3 bg-gray-200 border-none"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="address" className="block font-medium">
                Business address
              </label>
              <TextArea
                disabled={isSubmitting}
                id="address"
                name="address"
                placeholder="Enter business address"
                value={formData.address}
                onChange={handleTextChange}
                className="pl-3 bg-gray-200 border-none"
              ></TextArea>
            </div>
          </div>
        </div>
        <div className="flex justify-center">
          <Button
            disabled={isSubmitting}
            type="submit"
            className="text-lg w-full md:w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-2"
          >
            {isSubmitting ? (<Loader2 className="animate-spin h-5 w-5" />) : "Save"}
          </Button>
        </div>
      </form>
    </div>
    </Modal>
  );
}